import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getRewardCodes,
  createRewardCode,
  updateRewardCode,
  deleteRewardCode,
  getRewardCampaign,
  updateRewardCampaign,
} from "api/bSurveysApi";

/**
 * Hook for managing reward codes for a survey template
 */
export function useRewardCodes(templateId) {
  const queryClient = useQueryClient();

  const { data: rewardCodes, isLoading, error, refetch } = useQuery({
    queryKey: ["rewardCodes", templateId],
    queryFn: () => getRewardCodes(templateId).then((res) => res.data),
    enabled: !!templateId && templateId !== 'new',
  });

  const createMutation = useMutation({
    mutationFn: (data) => createRewardCode(templateId, data),
    onSuccess: () => {
      queryClient.invalidateQueries(["rewardCodes", templateId]);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ rewardId, data }) => updateRewardCode(templateId, rewardId, data),
    onSuccess: () => {
      queryClient.invalidateQueries(["rewardCodes", templateId]);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (rewardId) => deleteRewardCode(templateId, rewardId),
    onSuccess: () => {
      queryClient.invalidateQueries(["rewardCodes", templateId]);
    },
  });

  return {
    rewardCodes: rewardCodes || [],
    isLoading,
    error,
    refetch,
    createReward: createMutation.mutate,
    createRewardAsync: createMutation.mutateAsync,
    isCreating: createMutation.isPending,
    updateReward: updateMutation.mutate,
    updateRewardAsync: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
    deleteReward: deleteMutation.mutate,
    deleteRewardAsync: deleteMutation.mutateAsync,
    isDeleting: deleteMutation.isPending,
  };
}

/**
 * Hook for managing reward campaign settings for a survey template
 */
export function useRewardCampaign(templateId) {
  const queryClient = useQueryClient();

  const { data: campaign, isLoading, error, refetch } = useQuery({
    queryKey: ["rewardCampaign", templateId],
    queryFn: () => getRewardCampaign(templateId).then((res) => res.data),
    enabled: !!templateId && templateId !== 'new',
  });

  const updateMutation = useMutation({
    mutationFn: (data) => updateRewardCampaign(templateId, data),
    onSuccess: () => {
      queryClient.invalidateQueries(["rewardCampaign", templateId]);
    },
  });

  return {
    campaign: campaign || {
      enabled: false,
      run_until_limit_reached: false,
      start_date: null,
      end_date: null,
      is_active: false,
    },
    isLoading,
    error,
    refetch,
    updateCampaign: updateMutation.mutate,
    updateCampaignAsync: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
  };
}